import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class VisionService {
    private VISION_API_URL = `https://vision.googleapis.com/v1/images:annotate?key=${environment.googleCloudVisionApiKey}`;

    constructor(private http: HttpClient) { }

    /**
     * Public method to fetch an image from a URL, convert to base64,
     * then call Google Vision to detect text.
     */
    async detectTextFromImageUrl(imageUrl: string): Promise<string> {
        const base64Image = await this.fetchImageAsBase64(imageUrl);
        return this.detectTextFromBase64(base64Image);
    }

    /**
     * Convert an image URL to Base64 by fetching the image as a blob.
     */
    private async fetchImageAsBase64(imageUrl: string): Promise<string> {
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                // reader.result is something like "data:image/png;base64,iVBORw0KG..."
                // We typically strip the "data:*/*;base64," prefix so Vision only gets the raw base64
                const dataUrl = reader.result as string;
                const base64 = dataUrl.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Call Google Vision API to detect text in a base64-encoded image.
     * Returns the full text recognized by Vision.
     */
    private detectTextFromBase64(base64String: string): Promise<string> {
        const requestBody = {
            requests: [
                {
                    image: { content: base64String },
                    features: [{ type: 'TEXT_DETECTION' }]
                }
            ]
        };

        return new Promise((resolve, reject) => {
            this.http.post<any>(this.VISION_API_URL, requestBody).subscribe({
                next: (response) => {
                    // Vision returns array: responses[0].fullTextAnnotation.text
                    const fullText =
                        response?.responses?.[0]?.fullTextAnnotation?.text || '';
                    resolve(fullText);
                },
                error: (err) => {
                    reject(err);
                }
            });
        });
    }
}
